<template>
  <div class="tab-content">
    <div class="stats-grid stats-grid--2">
      <div class="metric-card metric-card--expenses">
        <div class="metric-card__header">
          <span class="metric-card__title">Кол-во расходов</span>
          <span class="metric-card__icon">🧾</span>
        </div>
        <span class="metric-card__value">
          {{ totals.expensesCount }}
        </span>
        <span class="metric-card__sub">за выбранный период</span>
      </div>
      <div class="metric-card metric-card--invoices">
        <div class="metric-card__header">
          <span class="metric-card__title">Сумма расходов</span>
          <span class="metric-card__icon">💸</span>
        </div>
        <span class="metric-card__value">
          {{ formatMoney(totals.expensesTotalAmount) }}
        </span>
        <span class="metric-card__sub">операционные затраты</span>
      </div>
    </div>
    <div v-if="hasChartData" class="card chart-card">
      .
      <div class="chart-card__header">
        <h4>Динамика расходов по периодам</h4>
        <div class="filter-tabs">
          <button
            class="filter-tab"
            :class="{ active: effectiveChartType === 'line' }"
            :disabled="effectiveChartType === 'line'"
            @click="chartType = 'line'"
          >
            Линейный
          </button>
          <button
            v-if="!barTooManyPoints"
            class="filter-tab"
            :class="{ active: effectiveChartType === 'bar' }"
            :disabled="effectiveChartType === 'bar'"
            @click="chartType = 'bar'"
          >
            Столбчатый
          </button>
        </div>
      </div>
      <BarChart
        v-if="effectiveChartType === 'bar'"
        :series="chartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
      <LineChart
        v-else-if="effectiveChartType === 'line'"
        :series="chartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
    </div>
    <div v-if="hasStatusData" class="card status-card">
      <div class="status-card__header">
        <h4>Расходы по категориям</h4>
        <div class="status-card__header-right">
          <div class="status-card__total">
            Всего:
            <strong>{{ totals.expensesCount }} шт.</strong>
            на
            <strong>
              {{ formatMoney(totals.expensesTotalAmount) }}
            </strong>
          </div>
          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'donut' }"
              :disabled="statusViewType === 'donut'"
              @click="statusViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'bars' }"
              :disabled="statusViewType === 'bars'"
              @click="statusViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>
      <DonutChart
        v-if="statusViewType === 'donut'"
        :slices="categorySlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <PercentageLineChart
        v-else
        :slices="categorySlices"
        :format-value="formatMoney"
      />
    </div>
    <div v-if="hasPaymentSourceData" class="card status-card">
      <div class="status-card__header">
        <h4>Расходы по источникам</h4>
        <div class="status-card__header-right">
          <div class="status-card__total">
            Всего:
            <strong>{{ totals.expensesCount }} шт.</strong>
            на
            <strong>
              {{ formatMoney(totals.expensesTotalAmount) }}
            </strong>
          </div>
          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: paymentSourceViewType === 'donut' }"
              :disabled="paymentSourceViewType === 'donut'"
              @click="paymentSourceViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: paymentSourceViewType === 'bars' }"
              :disabled="paymentSourceViewType === 'bars'"
              @click="paymentSourceViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>
      <DonutChart
        v-if="paymentSourceViewType === 'donut'"
        :slices="paymentSourceSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <PercentageLineChart
        v-else
        :slices="paymentSourceSlices"
        :format-value="formatMoney"
      />
    </div>
    <div v-if="hasSpenderData" class="card status-card">
      <div class="status-card__header">
        <h4>Расходы по сотрудникам</h4>
        <div class="status-card__header-right">
          <div class="status-card__total">
            Всего:
            <strong>{{ totals.expensesCount }} шт.</strong>
            на
            <strong>
              {{ formatMoney(totals.expensesTotalAmount) }}
            </strong>
          </div>
          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: spenderViewType === 'donut' }"
              :disabled="spenderViewType === 'donut'"
              @click="spenderViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: spenderViewType === 'bars' }"
              :disabled="spenderViewType === 'bars'"
              @click="spenderViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>
      <DonutChart
        v-if="spenderViewType === 'donut'"
        :slices="spenderSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <PercentageLineChart
        v-else
        :slices="spenderSlices"
        :format-value="formatMoney"
      />
    </div>
  </div>
</template>

<script>
import LineChart from "@/components/Dashboard/Charts/LineChart.vue";
import BarChart from "@/components/Dashboard/Charts/BarChart.vue";
import DonutChart from "@/components/Dashboard/Charts/DonutChart.vue";
import PercentageLineChart from "@/components/Dashboard/Charts/PercentageLineChart.vue";

import { useExpenseCategoryStore } from "@/stores/expenseCategoryStore";
import { usePaymentSourceStore } from "@/stores/paymentSourceStore";
import { useCompanyUserStore } from "@/stores/companyUserStore";

import expenseCategoriesApi from "@/services/api/expenseCategoriesApi";
import paymentSourcesApi from "@/services/api/paymentSourcesApi";

import { getCompanyId } from "@/utils/userUtils";
import {
  formatMoney,
  formatChartValue,
  formatDisplayDate,
} from "@/utils/dashboardFormatters";

const CATEGORY_COLORS = [
  "#4c6ef5",
  "#00bfa5",
  "#ca8a04",
  "#f97316",
  "#a855f7",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
];

const CHART_TYPE_KEY = "dashboard:chart:chartType";
const STATUS_VIEW_TYPE_KEY = "dashboard:chart:statusViewType";
const PAYMENT_SOURCE_VIEW_TYPE_KEY = "dashboard:chart:paymentSourceViewType";
const SPENDER_VIEW_TYPE_KEY = "dashboard:chart:spenderViewType";
const VALID_CHART_TYPES = ["line", "bar"];
const VALID_STATUS_VIEW_TYPES = ["donut", "bars"];
const MAX_BAR_POINTS = 60;

export default {
  name: "DashboardExpenseTab",

  components: {
    LineChart,
    BarChart,
    DonutChart,
    PercentageLineChart,
  },

  props: {
    periods: {
      type: Array,
      required: true,
    },
  },

  data() {
    const savedChartType = localStorage.getItem(CHART_TYPE_KEY);
    const initialChartType = VALID_CHART_TYPES.includes(
      savedChartType
    )
      ? savedChartType
      : "line";

    const savedStatusViewType = localStorage.getItem(
      STATUS_VIEW_TYPE_KEY
    );
    const initialStatusViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedStatusViewType
    )
      ? savedStatusViewType
      : "donut";

    const savedSourceViewType = localStorage.getItem(
      PAYMENT_SOURCE_VIEW_TYPE_KEY
    );
    const initialSourceViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedSourceViewType
    )
      ? savedSourceViewType
      : "donut";

    const savedSpenderViewType = localStorage.getItem(
      SPENDER_VIEW_TYPE_KEY
    );
    const initialSpenderViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedSpenderViewType
    )
      ? savedSpenderViewType
      : "donut";

    return {
      categoryStore: useExpenseCategoryStore(),
      sourceStore: usePaymentSourceStore(),
      companyUserStore: useCompanyUserStore(),
      chartType: initialChartType,
      effectiveChartType: initialChartType,
      statusViewType: initialStatusViewType,
      paymentSourceViewType: initialSourceViewType,
      spenderViewType: initialSpenderViewType,
      chartSeries: [
        { key: "expenses", label: "Расходы", color: "#d64747" },
      ],
    };
  },

  computed: {
    hasChartData() {
      return (
        this.periods.length > 1 &&
        this.chartPoints.some((p) =>
          Object.values(p.values).some((v) => v > 0)
        )
      );
    },

    hasStatusData() {
      return this.categorySlices.some((s) => s.value > 0);
    },

    hasPaymentSourceData() {
      return this.paymentSourceSlices.some((s) => s.value > 0);
    },

    hasSpenderData() {
      return this.spenderSlices.some((s) => s.value > 0);
    },

    barTooManyPoints() {
      return this.periods.length > MAX_BAR_POINTS;
    },

    totals() {
      return this.periods.reduce(
        (acc, period) => {
          acc.expensesCount += period.expenses_count || 0;
          acc.expensesTotalAmount +=
            parseFloat(period.expenses_total_amount) || 0;

          for (const [catId, data] of Object.entries(
            period.expenses_by_category || {}
          )) {
            if (!acc.expensesByCategory[catId]) {
              acc.expensesByCategory[catId] = { count: 0, amount: 0 };
            }
            acc.expensesByCategory[catId].count += data.count || 0;
            acc.expensesByCategory[catId].amount +=
              parseFloat(data.amount) || 0;
          }

          for (const [sourceId, data] of Object.entries(
            period.expenses_by_payment_source || {}
          )) {
            if (!acc.expensesByPaymentSource[sourceId]) {
              acc.expensesByPaymentSource[sourceId] = { count: 0, amount: 0 };
            }
            acc.expensesByPaymentSource[sourceId].count += data.count || 0;
            acc.expensesByPaymentSource[sourceId].amount +=
              parseFloat(data.amount) || 0;
          }

          for (const [spenderId, data] of Object.entries(
            period.expenses_by_spender || {}
          )) {
            if (!acc.expensesBySpender[spenderId]) {
              acc.expensesBySpender[spenderId] = { count: 0, amount: 0 };
            }
            acc.expensesBySpender[spenderId].count += data.count || 0;
            acc.expensesBySpender[spenderId].amount +=
              parseFloat(data.amount) || 0;
          }

          return acc;
        },
        {
          expensesCount: 0,
          expensesTotalAmount: 0,
          expensesByCategory: {},
          expensesByPaymentSource: {},
          expensesBySpender: {},
        }
      );
    },

    chartPoints() {
      return this.periods.map((p) => ({
        label: formatDisplayDate(p.period_start, p.period_end),
        values: {
          expenses: parseFloat(p.expenses_total_amount) || 0,
        },
      }));
    },

    categories() {
      return Object.entries(this.totals.expensesByCategory).map(
        ([key, data], index) => {
          const stored = this.categoryStore.expenseCategories.find(
            (c) => String(c.id) === String(key)
          );
          const label = stored
            ? `${stored.emoji} ${stored.name}`
            : `Категория ${key}`;

          return {
            key,
            label,
            color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
            count: data.count,
            amount: data.amount,
          };
        }
      );
    },

    categorySlices() {
      return this.categories.map((c) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        value: c.amount,
        count: c.count,
      }));
    },

    paymentSources() {
      return Object.entries(this.totals.expensesByPaymentSource).map(
        ([key, data], index) => {
          let label;
          if (!key || key === "null") {
            label = "Без источника";
          } else {
            const stored = this.sourceStore.sources.find(
              (s) => String(s.id) === String(key)
            );
            label = stored ? stored.name : `Источник ${key}`;
          }

          return {
            key,
            label,
            color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
            count: data.count,
            amount: data.amount,
          };
        }
      );
    },

    paymentSourceSlices() {
      return this.paymentSources.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        value: s.amount,
        count: s.count,
      }));
    },

    spenders() {
      return Object.entries(this.totals.expensesBySpender).map(
        ([key, data], index) => {
          let label;
          if (!key || key === "null") {
            label = "Не указан";
          } else {
            const stored = this.companyUserStore.companyUsers.find(
              (u) => String(u.user_id) === String(key)
            );
            label = stored ? stored.full_name : `Пользователь ${key}`;
          }

          return {
            key,
            label,
            color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
            count: data.count,
            amount: data.amount,
          };
        }
      );
    },

    spenderSlices() {
      return this.spenders.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        value: s.amount,
        count: s.count,
      }));
    },
  },

  watch: {
    chartType(value) {
      localStorage.setItem(CHART_TYPE_KEY, value);
      if (value === "bar" && this.barTooManyPoints) {
        this.effectiveChartType = "line";
      } else {
        this.effectiveChartType = value;
      }
    },

    barTooManyPoints(tooMany) {
      if (tooMany && this.chartType === "bar") {
        this.effectiveChartType = "line";
      } else {
        this.effectiveChartType = this.chartType;
      }
    },

    statusViewType(value) {
      localStorage.setItem(STATUS_VIEW_TYPE_KEY, value);
    },

    paymentSourceViewType(value) {
      localStorage.setItem(PAYMENT_SOURCE_VIEW_TYPE_KEY, value);
    },

    spenderViewType(value) {
      localStorage.setItem(SPENDER_VIEW_TYPE_KEY, value);
    },

    "totals.expensesByCategory"(newVal) {
      this.fetchMissingCategories(Object.keys(newVal));
    },

    "totals.expensesByPaymentSource"(newVal) {
      this.fetchMissingPaymentSources(Object.keys(newVal));
    },

    "totals.expensesBySpender"(newVal) {
      this.fetchMissingSpenders(Object.keys(newVal));
    },
  },

  methods: {
    formatMoney,
    formatChartValue,

    async fetchMissingCategories(ids) {
      const companyId = getCompanyId();

      const missingIds = ids.filter(
        (id) =>
          !this.categoryStore.expenseCategories.some(
            (c) => String(c.id) === String(id)
          )
      );

      if (!missingIds.length) return;

      const results = await Promise.allSettled(
        missingIds.map((id) =>
          expenseCategoriesApi.show(companyId, id)
        )
      );

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          this.categoryStore.expenseCategories.push(
            result.value.data.result
          );
        }
      });
    },

    async fetchMissingPaymentSources(ids) {
      const companyId = getCompanyId();

      const validIds = ids.filter((id) => id && id !== "null" && id !== "");
      const missingIds = validIds.filter(
        (id) =>
          !this.sourceStore.sources.some(
            (s) => String(s.id) === String(id)
          )
      );

      if (!missingIds.length) return;

      const results = await Promise.allSettled(
        missingIds.map((id) => paymentSourcesApi.show(companyId, id))
      );

      results.forEach((result) => {
        if (result.status === "fulfilled") {
          this.sourceStore.sources.push(result.value.data.result);
        }
      });
    },

    async fetchMissingSpenders(ids) {
      const validIds = ids.filter((id) => id && id !== "null" && id !== "");
      const missingIds = validIds.filter(
        (id) =>
          !this.companyUserStore.companyUsers.some(
            (u) => String(u.user_id) === String(id)
          )
      );

      if (!missingIds.length) return;

      await this.companyUserStore.fetchCompanyUsers({ limit: 1000 });
    },
  },

  async created() {
    if (this.chartType === "bar" && this.barTooManyPoints) {
      this.effectiveChartType = "line";
    }

    if (!this.categoryStore.expenseCategories.length) {
      await this.categoryStore.fetchExpenseCategories();
    }
    await this.fetchMissingCategories(
      Object.keys(this.totals.expensesByCategory)
    );
    await this.fetchMissingPaymentSources(
      Object.keys(this.totals.expensesByPaymentSource)
    );
    await this.fetchMissingSpenders(
      Object.keys(this.totals.expensesBySpender)
    );
  },
};
</script>

<style scoped>
.tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-grid {
  display: grid;
  gap: 16px;
}

.stats-grid--2 {
  grid-template-columns: repeat(2, 1fr);
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--base-in-indent);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-top: 3px solid transparent;
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: "";
  position: absolute;
  inset: 0;
  opacity: 0.04;
  pointer-events: none;
}

.metric-card--expenses {
  border-top-color: #d64747;
}
.metric-card--expenses::before {
  background: #d64747;
}
.metric-card--invoices {
  border-top-color: #4c6ef5;
}
.metric-card--invoices::before {
  background: #4c6ef5;
}

.metric-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.metric-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-card__icon {
  font-size: 22px;
  line-height: 1;
}

.metric-card__value {
  font-size: 28px;
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1;
  letter-spacing: -0.5px;
}

.metric-card__sub {
  font-size: 12px;
  color: #bbb;
  margin-top: auto;
}

.chart-card {
  gap: 16px;
}

.chart-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  background: var(--color-grey);
  padding: 3px;
  border-radius: 8px;
  flex-shrink: 0;
}

.filter-tab {
  padding: 7px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #888;
  font-family: "Roboto", sans-serif;
  font-weight: 500;
}

.filter-tab.active {
  background: #fff;
  color: var(--color-dark);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.filter-tab:disabled {
  cursor: default;
  pointer-events: none;
}

.status-card {
  gap: 20px;
}

.status-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.status-card__header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.status-card__total {
  font-size: 13px;
  color: #999;
  white-space: nowrap;
}

.status-card__total strong {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-dark);
}

@media screen and (max-width: 767px) {
  .stats-grid--2 {
    gap: 12px;
  }

  .metric-card__value {
    font-size: 22px;
  }

  .chart-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header-right {
    width: 100%;
    justify-content: space-between;
  }
}

@media screen and (max-width: 480px) {
  .metric-card {
    padding: 14px;
    gap: 8px;
  }
  .metric-card__value {
    font-size: 19px;
  }
  .metric-card__icon {
    font-size: 18px;
  }
}
</style>
